import type { UIEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  AccountHolderOwner,
  AccountHoldersAudienceFilter,
  AccountHoldersFilters,
} from '../../../types/features/accountHolders';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import accountHoldersService from '../services/accountHoldersService';
import {
  buildAccountHoldersSearchParams,
  getAccountHoldersFiltersFromSearchParams,
} from '../utils/accountHoldersQueryParams';

const PAGE_BATCH_SIZE = 500;

export const useAccountHoldersPageLogic = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = getAccountHoldersFiltersFromSearchParams(searchParams);

  const [search, setSearch] = useState<string>(initialFilters.search);
  const [audienceFilter, setAudienceFilter] = useState<AccountHoldersAudienceFilter>(initialFilters.audience);
  const [owners, setOwners] = useState<AccountHolderOwner[]>([]);
  const [totalOwners, setTotalOwners] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [deleteError, setDeleteError] = useState<string>('');
  const [deletingOwnerId, setDeletingOwnerId] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const requestIdRef = useRef(0);
  const isInternalSearchParamsUpdateRef = useRef(false);

  const debouncedSearch = useDebouncedValue(search, 500);

  const loadOwners = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const accumulatedOwners: AccountHolderOwner[] = [];
    let pageToLoad = 1;
    let hasNext = true;
    let totalOwnerCount = 0;

    setLoading(true);
    setLoadingMore(false);
    setError('');
    setDeleteError('');
    setOwners([]);
    setTotalOwners(0);
    setHasNextPage(false);

    try {
      while (hasNext) {
        const response = await accountHoldersService.getAccountHolders({
          page: pageToLoad,
          pageSize: PAGE_BATCH_SIZE,
          search: '',
          includeWithoutStores: true,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        const fetchedOwners = Array.isArray(response?.owners) ? response.owners : [];
        accumulatedOwners.push(...fetchedOwners);
        totalOwnerCount = typeof response?.total_owners === 'number'
          ? response.total_owners
          : accumulatedOwners.length;
        hasNext = Boolean(response?.has_next);
        pageToLoad += 1;

        setOwners([...accumulatedOwners]);
        setTotalOwners(totalOwnerCount);
        setLoadingMore(hasNext);
      }

      setHasNextPage(false);
    } catch (_error: unknown) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setError('Failed to load account holders. Please try again.');
      setOwners([]);
      setTotalOwners(0);
      setHasNextPage(false);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadOwners();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadOwners]);

  useEffect(() => {
    const filters: AccountHoldersFilters = {
      search,
      audience: audienceFilter,
    };

    isInternalSearchParamsUpdateRef.current = true;
    setSearchParams(buildAccountHoldersSearchParams(filters), { replace: true });
  }, [audienceFilter, search, setSearchParams]);

  useEffect(() => {
    if (isInternalSearchParamsUpdateRef.current) {
      isInternalSearchParamsUpdateRef.current = false;
      return;
    }

    const nextFilters = getAccountHoldersFiltersFromSearchParams(searchParams);

    if (nextFilters.search !== search) setSearch(nextFilters.search);
    if (nextFilters.audience !== audienceFilter) {
      setAudienceFilter(nextFilters.audience);
    }
  }, [audienceFilter, search, searchParams]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleAudienceFilterChange = useCallback((nextAudienceFilter: AccountHoldersAudienceFilter) => {
    setAudienceFilter(nextAudienceFilter);
  }, []);

  const handleTableScroll = useCallback((_event: UIEvent<HTMLDivElement>) => {
    // All owner pages are loaded up front so search and tab filters stay local and complete.
  }, []);

  const handleOwnerClick = useCallback(
    (owner: AccountHolderOwner) => {
      navigate(`/dashboard/account-holders/${owner.user_id}`, { state: { owner } });
    },
    [navigate],
  );

  const handleDeleteLead = useCallback(async (owner: AccountHolderOwner) => {
    if (!owner || owner.no_of_stores > 0) {
      return;
    }

    const ownerLabel = owner.name || owner.email_address || 'this lead';
    const confirmed = window.confirm(`Delete ${ownerLabel}? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setDeletingOwnerId(owner.user_id);
    setDeleteError('');

    try {
      await accountHoldersService.deleteAccountHolderLead(owner.user_id);
      setOwners((currentOwners) => currentOwners.filter((currentOwner) => currentOwner.user_id !== owner.user_id));
      setTotalOwners((currentTotal) => Math.max(0, currentTotal - 1));
    } catch (_error: unknown) {
      setDeleteError('Failed to delete lead. Please try again.');
    } finally {
      setDeletingOwnerId(null);
    }
  }, []);

  return {
    search: debouncedSearch,
    searchInput: search,
    audienceFilter,
    owners,
    totalOwners,
    loading,
    loadingMore,
    error,
    deleteError,
    deletingOwnerId,
    hasNextPage,
    handleSearchChange,
    handleAudienceFilterChange,
    handleTableScroll,
    handleOwnerClick,
    handleDeleteLead,
  };
};
